import { useQuery } from "@tanstack/react-query";

interface SnapshotRow {
  takenAt: number;
  online: boolean;
  signal: number | null;
  rentableCount: number | null;
  totalCount: number | null;
}

interface ApiEnvelope<T> {
  data: T;
}

async function fetchSnapshots(id: string, hours: number): Promise<SnapshotRow[]> {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/cabinets/${id}/snapshots?hours=${hours}`);
  if (!res.ok) throw new Error(`snapshots → ${res.status}`);
  const json = (await res.json()) as ApiEnvelope<SnapshotRow[]>;
  return json.data;
}

export function SignalChart({ cabinetId, hours = 24 }: { cabinetId: string; hours?: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["snapshots", cabinetId, hours],
    queryFn: () => fetchSnapshots(cabinetId, hours),
    refetchInterval: 60_000,
  });

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-200">Signal · last {hours}h</h2>
        <span className="text-xs text-zinc-500">
          {isLoading ? "loading…" : `${data?.length ?? 0} samples`}
        </span>
      </div>
      <ChartBody rows={data ?? []} />
    </div>
  );
}

function ChartBody({ rows }: { rows: SnapshotRow[] }) {
  if (rows.length < 2) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-zinc-500">
        No history yet — cron snapshots every 5 min, give it a moment.
      </div>
    );
  }

  const W = 800;
  const H = 160;
  const PADX = 32;
  const PADY = 16;
  const innerW = W - PADX * 2;
  const innerH = H - PADY * 2;

  const minT = rows[0]!.takenAt;
  const maxT = rows[rows.length - 1]!.takenAt;
  const span = Math.max(1, maxT - minT);

  // Signal goes 0–31 per Bajie spec.
  const x = (t: number) => PADX + ((t - minT) / span) * innerW;
  const y = (s: number) => PADY + (1 - Math.max(0, Math.min(31, s)) / 31) * innerH;

  const points = rows
    .filter((r) => r.signal !== null)
    .map((r) => `${x(r.takenAt)},${y(r.signal!)}`)
    .join(" ");

  // Render online/offline as background bands.
  const bands: { x1: number; x2: number; online: boolean }[] = [];
  for (let i = 0; i < rows.length - 1; i++) {
    bands.push({
      x1: x(rows[i]!.takenAt),
      x2: x(rows[i + 1]!.takenAt),
      online: rows[i]!.online,
    });
  }

  // y-axis ticks at 0, 19 (Bajie connectivity threshold), 31.
  const ticks = [0, 19, 31];

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
        {bands.map((b, i) => (
          <rect
            key={i}
            x={b.x1}
            y={PADY}
            width={Math.max(0, b.x2 - b.x1)}
            height={innerH}
            fill={b.online ? "rgba(168,85,247,0.08)" : "rgba(239,68,68,0.10)"}
          />
        ))}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PADX}
              x2={W - PADX}
              y1={y(t)}
              y2={y(t)}
              stroke={t === 19 ? "rgba(245,158,11,0.40)" : "rgba(82,82,91,0.30)"}
              strokeDasharray={t === 19 ? "3 3" : undefined}
            />
            <text
              x={PADX - 6}
              y={y(t) + 3}
              textAnchor="end"
              fontSize="10"
              fill="rgba(161,161,170,0.7)"
              fontFamily="ui-monospace, monospace"
            >
              {t}
            </text>
          </g>
        ))}
        <defs>
          <linearGradient id="signalLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#a855f7" />
            <stop offset="100%" stopColor="#f97316" />
          </linearGradient>
        </defs>
        <polyline
          fill="none"
          stroke="url(#signalLine)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
        <text
          x={W - PADX}
          y={H - 2}
          textAnchor="end"
          fontSize="9"
          fill="rgba(113,113,122,0.8)"
          fontFamily="ui-monospace, monospace"
        >
          {new Date(minT).toLocaleTimeString()} → {new Date(maxT).toLocaleTimeString()}
        </text>
      </svg>
      <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-3 rounded-sm bg-brand-purple/30" /> online
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-3 rounded-sm bg-rose-500/30" /> offline
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-px w-3 bg-amber-400/60" /> 19 = Bajie connectivity threshold
        </span>
      </div>
    </div>
  );
}
